import {
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { Applicant } from "types/application";
import { UseQueryResult } from "@tanstack/react-query";
import { BasicInformation } from "../slices/BasicInformation";
import { IdentityVerificationReportSlice } from "views/applications-id-report/slices/IdentityVerificationReportSlice";
import { IncomeReportSlice } from "views/applications-id-report/slices/IncomeReportSlice";
import { HousingHistoryReportSlice } from "views/applications-id-report/slices/HousingHistoryReportSlice";
import { CreditReportSlice } from "views/applications-id-report/slices/CreditReportSlice";
import { CriminalReportSlice } from "views/applications-id-report/slices/CriminalReportSlice";
import { EvictionReportSlice } from "views/applications-id-report/slices/EvictionReportSlice";
import { Application } from "types/application";
import { useContext, useEffect } from "react";
import { PrintAllContext } from "../providers/PrintProvider";
import { IdentityVerificationSettings } from "types/landlord/verificationTemplate";
import { ApplicantSettings } from "shared/types";
import { DocumentPDFViewer } from "../slices/AdditionalDocumentsSlice/components/DocumentPDFViewer";
import { DisclosureSlice } from "../slices/DisclosureSlice";

interface PrintApplicationProps {
  verifications: Verification[];
  applicant: Applicant;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
}

export const PrintApplication = ({
  verifications,
  applicant,
  applicationMeta
}: PrintApplicationProps) => {
  const { setPrintVerificationsState, setPrintingConditions } =
    useContext(PrintAllContext);

  useEffect(() => {
    if (verifications.length) {
      const verificationWithConditions = verifications.filter(
        (v) =>
          [
            VerificationNames.VerifiedIncomeVerification,
            VerificationNames.CreditReport
          ].includes(v.name) &&
          v.completed_at &&
          ![
            VerificationStatus.Pending,
            VerificationStatus.Canceled,
            VerificationStatus.Started
          ].includes(v.status) &&
          v.name === VerificationNames.VerifiedIncomeVerification &&
          v.status !== VerificationStatus.FinishedWithWarnings
      );

      if (verificationWithConditions.length)
        setPrintingConditions(
          verificationWithConditions.reduce(
            (acc, v) => {
              acc[String(v.id)] = false;
              return acc;
            },
            {} as Record<string, boolean>
          )
        );

      setPrintVerificationsState(
        verifications.reduce(
          (acc, v) => {
            if (
              v.completed_at &&
              ![
                VerificationStatus.Pending,
                VerificationStatus.Canceled,
                VerificationStatus.Started
              ].includes(v.status)
            )
              acc[String(v.id)] = false;

            return acc;
          },
          {} as Record<string, boolean>
        )
      );
    }
  }, [verifications]);

  return (
    <div>
      <div className="flex h-full flex-col rounded-lg border border-alpha-100 bg-white pt-0 pl-4 pr-6 shadow-xs print:w-full print:border-none lg:flex-row">
        <div className="min-h-full w-full overflow-y-auto md:pl-6 print:overflow-y-visible print:border-none print:p-0 lg:border-l lg:border-alpha-40">
          {verifications
            .filter(
              (v) => {
                return (
                  v.completed_at &&
                  ![
                    VerificationStatus.Pending,
                    VerificationStatus.Canceled,
                    VerificationStatus.Started
                  ].includes(v.status)
                );
              },
              {} as Record<string, boolean>
            )
            .reduce(
              (
                acc: Verification[],
                v: Verification,
                _idx,
                rest: Verification[]
              ) => {
                if (
                  v.name === VerificationNames.IncomeVerification &&
                  rest.some(
                    (restV) =>
                      restV.name ===
                      VerificationNames.VerifiedIncomeVerification
                  )
                )
                  return acc;

                if (
                  v.name === VerificationNames.VerifiedIncomeVerification &&
                  acc.some(
                    (accV) => accV.name === VerificationNames.IncomeVerification
                  )
                )
                  return acc;

                return [...acc, v];
              },
              []
            )
            .map((verification) => {
              switch (verification.name) {
                case VerificationNames.RentalApplication:
                  return (
                    <BasicInformation
                      key={verification.id}
                      verificationId={verification.id}
                      applicant={applicant}
                      applicationMeta={applicationMeta}
                      email={applicant.email}
                      phone={applicant.phone}
                    />
                  );
                case VerificationNames.IdentityVerification:
                  return (
                    <div
                      className="print:break-before-page pt-2"
                      key={verification.id}
                    >
                      <IdentityVerificationReportSlice
                        requiresOverridesIdentityVerification={Boolean(
                          applicationMeta.data?.requires_overrides_identity_verification.applicants.some(
                            ({ id }) => id === applicant.id
                          )
                        )}
                        applicationId={String(applicationMeta.data?.id)}
                        verificationId={verification.id}
                        applicant={applicant}
                        templateIdentityVerificationSettings={
                          applicationMeta.data?.verification_template
                            ?.settings as IdentityVerificationSettings
                        }
                      />
                    </div>
                  );

                case VerificationNames.VerifiedIncomeVerification:
                case VerificationNames.IncomeVerification:
                  return (
                    <div
                      className="print:break-before-page pt-2"
                      key={verification.id}
                    >
                      <IncomeReportSlice
                        applicant={applicant}
                        screeningCriteria={
                          applicationMeta.data?.verification_template
                            ?.screening_criteria
                            ?.applicant_settings as ApplicantSettings
                        }
                      />
                    </div>
                  );
                case VerificationNames.HousingHistory:
                  return (
                    <div
                      className="print:break-before-page pt-2"
                      key={verification.id}
                    >
                      <HousingHistoryReportSlice
                        verificationId={verification.id}
                        applicant={applicant}
                      />
                    </div>
                  );
                case VerificationNames.CreditReport:
                  return (
                    <div className="print:break-before-page pt-2">
                      <CreditReportSlice
                        applicant={applicant}
                        verificationId={verification.id}
                        pastDueAccountType={
                          applicationMeta.data?.verification_template
                            ?.screening_criteria
                            ? applicationMeta.data.verification_template
                                .screening_criteria[
                                `${applicant.role}_settings`
                              ]?.past_due_account?.type
                            : ""
                        }
                        screeningCriteria={
                          applicationMeta.data?.verification_template
                            ?.screening_criteria
                            ?.applicant_settings as ApplicantSettings
                        }
                      />
                    </div>
                  );
                case VerificationNames.EvictionReport:
                  return (
                    <div className="print:break-before-page pt-2">
                      <EvictionReportSlice
                        verificationId={verification.id}
                        applicant={applicant}
                        screeningCriteria={
                          applicationMeta.data?.verification_template
                            ?.screening_criteria
                            ?.applicant_settings as ApplicantSettings
                        }
                      />
                    </div>
                  );
                case VerificationNames.CriminalReport:
                  return (
                    <div className="print:break-before-page pt-2">
                      <CriminalReportSlice
                        verificationId={verification.id}
                        applicant={applicant}
                        screeningCriteria={
                          applicationMeta.data?.verification_template
                            ?.screening_criteria
                            ?.applicant_settings as ApplicantSettings
                        }
                      />
                    </div>
                  );
                case VerificationNames.SubmissionDisclosure:
                  return (
                    <div className="print:break-before-page pt-2">
                      <DisclosureSlice
                        verificationId={verification.id}
                        applicant={applicant}
                      />
                    </div>
                  );
                default:
                  return null;
              }
            })}
          <DocumentPDFViewer applicantId={applicant.id} />
        </div>
      </div>
    </div>
  );
};
